import { ComponentType, ReactNode, forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, Copy, PanelLeft } from 'lucide-react';

import { GeometricFacetBackground } from './GeometricFacetBackground';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { NavLink } from '@/components/NavLink';
import { recordAnalyticsNavigation } from '@/lib/analyticsNavigation';
import { surfaceTonePalette } from '@/lib/surfaceTonePalette';

export type SurfaceSidebarTone = 'workspace' | 'admin' | 'vendor';

type ToneConfig = {
  headerBorderClass: string;
  footerBorderClass: string;
  groupLabelClass: string;
  collapsedMenuButtonHoverClass: string;
  collapsedMenuIconClass: string;
  profileHoverClass: string;
  profileAvatarBorderClass: string;
  profileAvatarFallbackClass: string;
  profileNameClass: string;
  profileRoleClass: string;
  profileChevronClass: string;
  headerBrandButtonClass: string;
  headerBrandPanelClass: string;
  headerTriggerHoverClass: string;
  headerTitleClass: string;
  headerSubtitleClass: string;
  navItemTextClass: string;
  navItemHoverClass: string;
  navItemActiveClass: string;
  depthGlowExpandedClass: string;
  depthGlowCollapsedClass: string;
  depthWashExpandedClass: string;
  depthWashCollapsedClass: string;
};

const toneConfig: Record<SurfaceSidebarTone, ToneConfig> = {
  workspace: {
    headerBorderClass: 'border-purple-500/20',
    footerBorderClass: 'border-purple-500/20',
    groupLabelClass: 'text-purple-200',
    collapsedMenuButtonHoverClass: 'hover:bg-purple-500/10',
    collapsedMenuIconClass: 'text-purple-300/70',
    profileHoverClass: 'hover:bg-purple-500/10',
    profileAvatarBorderClass: 'border-purple-500/30 group-hover:border-purple-300/60',
    profileAvatarFallbackClass: 'bg-purple-700 text-purple-100 text-xs',
    profileNameClass: 'text-purple-50',
    profileRoleClass: 'text-purple-300/60',
    profileChevronClass: 'text-purple-300/70',
    headerBrandButtonClass: 'bg-primary/10 hover:bg-primary/20',
    headerBrandPanelClass: 'border border-white/10 bg-primary/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] group-hover:border-primary/40 group-hover:bg-primary/25',
    headerTriggerHoverClass: 'hover:bg-purple-500/10',
    headerTitleClass: 'text-white',
    headerSubtitleClass: 'text-purple-200/80',
    navItemTextClass: 'text-purple-100',
    navItemHoverClass: 'hover:bg-purple-500/20 hover:text-white',
    navItemActiveClass: 'bg-purple-500/25 text-white font-medium',
    depthGlowExpandedClass:
      'absolute left-1/2 top-[-18%] h-72 w-[120%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.28),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthGlowCollapsedClass:
      'absolute left-1/2 top-[-14%] h-56 w-[160%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.28),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthWashExpandedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.18),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_56%,_rgba(196,181,253,0.24),_rgba(167,139,250,0)_34%)] opacity-95',
    depthWashCollapsedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.18),_rgba(255,255,255,0)_20%),radial-gradient(circle_at_50%_50%,_rgba(196,181,253,0.22),_rgba(167,139,250,0)_28%)] opacity-90',
  },
  admin: {
    headerBorderClass: 'border-rose-500/20',
    footerBorderClass: 'border-rose-500/20',
    groupLabelClass: 'text-rose-200',
    collapsedMenuButtonHoverClass: 'hover:bg-rose-500/10',
    collapsedMenuIconClass: 'text-rose-300/70',
    profileHoverClass: 'hover:bg-rose-500/10',
    profileAvatarBorderClass: 'border-rose-500/30 group-hover:border-rose-300/60',
    profileAvatarFallbackClass: 'bg-red-900 text-rose-100 text-xs',
    profileNameClass: 'text-rose-50',
    profileRoleClass: 'text-rose-300/60',
    profileChevronClass: 'text-rose-300/70',
    headerBrandButtonClass: 'bg-rose-500/10 hover:bg-rose-500/20',
    headerBrandPanelClass: 'bg-rose-500/20 text-rose-100',
    headerTriggerHoverClass: 'hover:bg-rose-500/10',
    headerTitleClass: 'text-white',
    headerSubtitleClass: 'text-rose-200/80',
    navItemTextClass: 'text-rose-100',
    navItemHoverClass: 'hover:bg-rose-500/20 hover:text-white',
    navItemActiveClass: 'bg-rose-500/25 text-white font-medium',
    depthGlowExpandedClass:
      'absolute left-1/2 top-[-18%] h-72 w-[120%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthGlowCollapsedClass:
      'absolute left-1/2 top-[-14%] h-56 w-[160%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthWashExpandedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.16),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_56%,_rgba(251,113,133,0.24),_rgba(244,63,94,0)_34%)] opacity-95',
    depthWashCollapsedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.16),_rgba(255,255,255,0)_20%),radial-gradient(circle_at_50%_50%,_rgba(251,113,133,0.22),_rgba(244,63,94,0)_28%)] opacity-90',
  },
  vendor: {
    headerBorderClass: 'border-emerald-500/20',
    footerBorderClass: 'border-emerald-500/20',
    groupLabelClass: 'text-emerald-200',
    collapsedMenuButtonHoverClass: 'hover:bg-emerald-500/10',
    collapsedMenuIconClass: 'text-emerald-300/70',
    profileHoverClass: 'hover:bg-emerald-500/10',
    profileAvatarBorderClass: 'border-emerald-500/30 group-hover:border-emerald-300/60',
    profileAvatarFallbackClass: 'bg-emerald-700 text-emerald-100 text-xs',
    profileNameClass: 'text-white',
    profileRoleClass: 'text-emerald-200/60',
    profileChevronClass: 'text-emerald-300/70',
    headerBrandButtonClass: 'bg-emerald-500/10 hover:bg-emerald-500/20',
    headerBrandPanelClass: 'bg-emerald-400/20 group-hover:bg-emerald-400/25',
    headerTriggerHoverClass: 'hover:bg-emerald-500/10',
    headerTitleClass: 'text-white',
    headerSubtitleClass: 'text-emerald-200/80',
    navItemTextClass: 'text-emerald-100',
    navItemHoverClass: 'hover:bg-emerald-500/20 hover:text-white',
    navItemActiveClass: 'bg-emerald-500/25 text-white font-medium',
    depthGlowExpandedClass:
      'absolute left-1/2 top-[-18%] h-72 w-[120%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthGlowCollapsedClass:
      'absolute left-1/2 top-[-14%] h-56 w-[160%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl',
    depthWashExpandedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.14),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_56%,_rgba(110,231,183,0.16),_rgba(16,185,129,0)_34%)] opacity-95',
    depthWashCollapsedClass:
      'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.14),_rgba(255,255,255,0)_20%),radial-gradient(circle_at_50%_50%,_rgba(110,231,183,0.14),_rgba(16,185,129,0)_28%)] opacity-90',
  },
};

function normalizeSidebarPath(value: string, origin = 'http://localhost') {
  try {
    const url = new URL(value, origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

export function getSurfaceSidebarToneConfig(tone: SurfaceSidebarTone) {
  return toneConfig[tone];
}

export function SurfaceSidebarBackdrop({
  tone,
  collapsed,
  idPrefix,
  testId,
}: {
  tone: SurfaceSidebarTone;
  collapsed: boolean;
  idPrefix: string;
  testId: string;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <GeometricFacetBackground
      tone={tone}
      idPrefix={idPrefix}
      testId={testId}
      className="pointer-events-none absolute inset-0 overflow-hidden [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.98)_0%,rgba(0,0,0,0.88)_34%,rgba(0,0,0,0.46)_56%,rgba(0,0,0,0.12)_74%,transparent_92%)]"
      glowClassName={collapsed ? config.depthGlowCollapsedClass : config.depthGlowExpandedClass}
      washClassName={collapsed ? config.depthWashCollapsedClass : config.depthWashExpandedClass}
      svgClassName={collapsed
        ? 'absolute top-[-12%] left-1/2 h-[112%] w-[156%] -translate-x-1/2 opacity-[0.78]'
        : 'absolute top-[-16%] left-1/2 h-[118%] w-[232%] -translate-x-1/2 opacity-[0.86]'}
    />
  );
}

function SurfaceSidebarEdgeSection({
  borderClassName,
  className,
  children,
}: {
  borderClassName: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('relative z-10 border-b p-4', borderClassName, className)}>
      {children}
    </div>
  );
}

export function SurfaceSidebarHeader({
  tone,
  commandPalette,
  children,
}: {
  tone: SurfaceSidebarTone;
  commandPalette?: ReactNode;
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <SidebarHeader className="p-0">
      <SurfaceSidebarEdgeSection borderClassName={config.headerBorderClass} className="flex flex-col gap-2 !py-2">
        {children}
        {commandPalette ? <div>{commandPalette}</div> : null}
      </SurfaceSidebarEdgeSection>
    </SidebarHeader>
  );
}

export function SurfaceSidebarContent({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <SidebarContent className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
      {children}
    </SidebarContent>
  );
}

export function SurfaceSidebarSection({
  tone,
  label,
  className,
  children,
}: {
  tone: SurfaceSidebarTone;
  label?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <SidebarGroup className={className}>
      {label ? <SidebarGroupLabel className={cn(config.groupLabelClass)}>{label}</SidebarGroupLabel> : null}
      <SidebarGroupContent>{children}</SidebarGroupContent>
    </SidebarGroup>
  );
}

export function SurfaceSidebarFooter({
  tone,
  bordered = true,
  children,
}: {
  tone: SurfaceSidebarTone;
  bordered?: boolean;
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <SidebarFooter className="p-0">
      <SurfaceSidebarEdgeSection borderClassName={cn(bordered && 'border-t', config.footerBorderClass)} className="space-y-0.5">
        {children}
      </SurfaceSidebarEdgeSection>
    </SidebarFooter>
  );
}

export function SurfaceSidebarBrandButton({
  tone,
  ariaLabel,
  onClick,
  children,
}: {
  tone: SurfaceSidebarTone;
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        'group flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg transition-all hover:scale-[1.02]',
        config.headerBrandButtonClass,
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function SurfaceSidebarBrandPanel({
  tone,
  className,
  children,
}: {
  tone: SurfaceSidebarTone;
  className?: string;
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg transition-all group-hover:shadow-sm', config.headerBrandPanelClass, className)}>
      {children}
    </div>
  );
}

export function SurfaceSidebarCopyButton({
  tone,
  ariaLabel,
  onClick,
  className,
}: {
  tone: SurfaceSidebarTone;
  ariaLabel: string;
  onClick: () => void;
  className?: string;
}) {
  const palette = surfaceTonePalette[tone];

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.stopPropagation();
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.stopPropagation();
        }
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        'pointer-events-none flex h-5 w-5 items-center justify-center rounded-md border border-transparent opacity-0 transition-all focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100',
        palette.copyButtonIdleClass,
        palette.copyButtonHoverClass,
        className,
      )}
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
}

// Sub-pixel headroom kept between the fitted text and its container. Font metrics differ
// slightly from layout to paint, so a label whose natural width lands exactly on the container
// width can still clip to an ellipsis (ENT-4071: "Customer Profile" at the 256px sidebar width).
// Always leaving a couple of pixels forces a shrink at the boundary instead of a truncation.
const FIT_EDGE_GUARD_PX = 2;

export function FitToWidthText({
  text,
  className,
  maxFontPx = 12,
  minFontPx = 9,
}: {
  text: ReactNode;
  className?: string;
  maxFontPx?: number;
  minFontPx?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const lastMeasuredTextRef = useRef<string | null>(null);
  const [fontPx, setFontPx] = useState(maxFontPx);

  const fit = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const available = container.clientWidth;
    // Width not laid out yet (first paint, or mounted while the sidebar is hidden/animating):
    // bail now and let the ResizeObserver re-run `fit` once the element gains width, rather than
    // measuring against 0 and locking the text at `maxFontPx`.
    if (!available) return;
    // Measure the TRUE single-line text width at `maxFontPx`. A `Range` over the span's contents
    // reports the text's laid-out geometry, which — unlike the span's own `scrollWidth` — is NOT
    // floored at the container width by the `truncate` (`overflow:hidden`) clip, so it exposes the
    // sub-pixel overflow that otherwise slips past as an ellipsis (ENT-4071). Fractional
    // `getBoundingClientRect().width` also avoids the integer rounding `scrollWidth` would impose.
    const prev = measure.style.fontSize;
    measure.style.fontSize = `${maxFontPx}px`;
    const range = document.createRange();
    range.selectNodeContents(measure);
    const natural = range.getBoundingClientRect().width;
    measure.style.fontSize = prev;
    if (!natural) return;
    const budget = Math.max(1, available - FIT_EDGE_GUARD_PX);
    if (natural <= budget) {
      setFontPx(maxFontPx);
      return;
    }
    const ratio = budget / natural;
    const next = Math.max(minFontPx, Math.floor(maxFontPx * ratio * 10) / 10);
    setFontPx(next);
  }, [maxFontPx, minFontPx]);

  // Observer setup is decoupled from JSX text identity so re-renders with the
  // same visible text don't churn the ResizeObserver.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fit]);

  // Re-fit when the rendered text content actually changes (handles ReactNode
  // children whose object identity differs but whose text is stable).
  useLayoutEffect(() => {
    const measure = measureRef.current;
    if (!measure) return;
    const currentText = measure.textContent ?? '';
    if (currentText === lastMeasuredTextRef.current) return;
    lastMeasuredTextRef.current = currentText;
    fit();
  });

  return (
    <div ref={containerRef} className={cn('w-full overflow-hidden', className)}>
      <span
        ref={measureRef}
        className="block truncate"
        style={{ fontSize: `${fontPx}px` }}
      >
        {text}
      </span>
    </div>
  );
}

function SurfaceSidebarIdentityRow({
  tone,
  title,
  subtitle,
  badges,
  trailingAccessory,
  showChevron = true,
  titleClassName,
  subtitleClassName,
  chevronClassName,
}: {
  tone: SurfaceSidebarTone;
  title: ReactNode;
  subtitle: ReactNode;
  badges?: ReactNode;
  trailingAccessory?: ReactNode;
  showChevron?: boolean;
  titleClassName: string;
  subtitleClassName: string;
  chevronClassName: string;
}) {
  const hasTrailingAccessory = Boolean(trailingAccessory);
  // Reserve right padding so fitted/truncated text excludes the absolutely
  // positioned overlay (copy button h-5 w-5 = 20px, chevron h-4 w-4 = 16px,
  // gap-1 = 4px between when both are present).
  const overlayPaddingClass = hasTrailingAccessory && showChevron
    ? 'pr-10'
    : hasTrailingAccessory
      ? 'pr-6'
      : showChevron
        ? 'pr-5'
        : '';
  const hasAccessoryOverlay = hasTrailingAccessory || showChevron;
  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 items-center">
      <div className={cn('min-w-0 flex-1', overlayPaddingClass)}>
        <div className="min-w-0">
          {typeof title === 'string'
            ? <FitToWidthText text={title} className={cn('text-base font-bold leading-6', titleClassName)} maxFontPx={16} minFontPx={11} />
            : <p className={cn('min-w-0 truncate text-base font-bold leading-6', titleClassName)}>{title}</p>}
        </div>
        {typeof subtitle === 'string'
          ? <FitToWidthText text={subtitle} className={cn('text-xs leading-4', subtitleClassName)} maxFontPx={12} minFontPx={9} />
          : <p className={cn('truncate text-xs', subtitleClassName)}>{subtitle}</p>}
        {badges ? <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">{badges}</div> : null}
      </div>
      {hasAccessoryOverlay ? (
        <div className="pointer-events-none absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-1">
          <span className="pointer-events-auto">{trailingAccessory}</span>
          {showChevron ? <ChevronDown className={cn('h-4 w-4 shrink-0', chevronClassName)} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export const SurfaceSidebarSwitcherButton = forwardRef<
  HTMLDivElement,
  {
    tone: SurfaceSidebarTone;
    collapsed: boolean;
    ariaLabel: string;
    title: ReactNode;
    subtitle: ReactNode;
    badges?: ReactNode;
    trailingAccessory?: ReactNode;
    className?: string;
  } & React.ComponentPropsWithoutRef<'div'>
>(function SurfaceSidebarSwitcherButton(
  {
    tone,
    collapsed,
    ariaLabel,
    title,
    subtitle,
    badges,
    trailingAccessory,
    className,
    ...rest
  },
  ref,
) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      {...rest}
      className={cn(
        'group flex cursor-pointer items-center gap-3 text-left transition-colors',
        config.headerTriggerHoverClass,
        collapsed ? 'mx-auto size-8 shrink-0 justify-center rounded-md p-2' : 'w-full min-w-0 rounded-lg px-2 py-0',
        className,
      )}
    >
      {collapsed ? (
        <ChevronDown className={cn('h-4 w-4', config.collapsedMenuIconClass)} />
      ) : (
        <SurfaceSidebarIdentityRow
          tone={tone}
          title={title}
          subtitle={subtitle}
          badges={badges}
          trailingAccessory={trailingAccessory}
          showChevron={false}
          titleClassName={config.headerTitleClass}
          subtitleClassName={config.headerSubtitleClass}
          chevronClassName={config.collapsedMenuIconClass}
        />
      )}
    </div>
  );
});

export function SurfaceSidebarSwitcherControl({
  collapsed,
  className,
  children,
}: {
  collapsed: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn(collapsed ? undefined : 'flex min-w-0 flex-1 items-center gap-1.5', className)}>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function SurfaceSidebarNavItemLink({
  tone,
  to,
  title,
  icon: Icon,
  collapsed,
  collapsedNavItemClass = '',
  tooltip,
  active,
  ariaLabel,
  trailingAccessory,
  collapsedAccessory,
  onClick,
  onMouseEnter,
  onFocus,
}: {
  tone: SurfaceSidebarTone;
  to: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  collapsed: boolean;
  collapsedNavItemClass?: string;
  tooltip?: string;
  active?: boolean;
  ariaLabel?: string;
  trailingAccessory?: ReactNode;
  collapsedAccessory?: ReactNode;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onFocus?: () => void;
}) {
  const location = useLocation();
  const config = getSurfaceSidebarToneConfig(tone);
  const labelClassName = collapsed
    ? 'max-w-0 translate-x-2 opacity-0'
    : 'max-w-[12rem] translate-x-0 opacity-100';

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={tooltip ?? title}>
        <NavLink
          to={to}
          aria-label={ariaLabel}
          onClick={(event) => {
            const currentPath = `${location.pathname}${location.search}${location.hash}`;
            const isCurrentRoute = active ?? normalizeSidebarPath(to, window.location.origin) === normalizeSidebarPath(currentPath, window.location.origin);
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
              onClick?.();
              return;
            }

            if (isCurrentRoute) {
              onClick?.();
              return;
            }

            recordAnalyticsNavigation({
              source: 'sidebar',
              surface: 'app_shell',
              intent: `sidebar:${title}`,
              to,
            });
            onClick?.();
          }}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          active={active}
          className={cn(
            'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-[gap,background-color,color,border-color]',
            config.navItemTextClass,
            config.navItemHoverClass,
            collapsedNavItemClass,
          )}
          activeClassName={config.navItemActiveClass}
        >
          <span className="relative flex shrink-0 items-center justify-center">
            <Icon className="h-4 w-4 shrink-0" />
            {collapsed && collapsedAccessory ? (
              <span className="pointer-events-none absolute -right-1 -top-1 flex items-center justify-center">
                {collapsedAccessory}
              </span>
            ) : null}
          </span>
          <span
            aria-hidden={collapsed}
            className={cn(
              'overflow-hidden whitespace-nowrap transition-[max-width,opacity,transform] duration-200 ease-linear',
              labelClassName,
            )}
          >
            {title}
          </span>
          {!collapsed && trailingAccessory ? (
            <span className="ml-auto flex shrink-0 items-center justify-center">
              {trailingAccessory}
            </span>
          ) : null}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SurfaceSidebarPortalLink({
  tone,
  to,
  title,
  icon: Icon,
  collapsed,
  collapsedNavItemClass = '',
  tooltip,
  active,
  onClick,
  accent = 'purple',
}: {
  tone: SurfaceSidebarTone;
  to: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  collapsed: boolean;
  collapsedNavItemClass?: string;
  tooltip?: string;
  active?: boolean;
  onClick?: (event: import('react').MouseEvent<HTMLAnchorElement>) => void;
  accent?: 'purple' | 'rose' | 'emerald';
}) {
  const palette = surfaceTonePalette[tone];
  const labelClassName = collapsed
    ? 'max-w-0 translate-x-2 opacity-0'
    : 'max-w-[12rem] translate-x-0 opacity-100';
  const idleClassName = `${palette.borderClass} ${palette.bgClass} ${palette.textClass}`;
  const destinationHoverClassName = {
    purple: 'hover:!bg-purple-800 hover:!border-purple-800 hover:!text-white',
    rose: 'hover:!bg-red-900 hover:!border-red-900 hover:!text-white',
    emerald: 'hover:!bg-emerald-800 hover:!border-emerald-800 hover:!text-white',
  }[accent];
  const destinationIconHoverClassName = {
    purple: 'group-hover:!text-white',
    rose: 'group-hover:!text-white',
    emerald: 'group-hover:!text-white',
  }[accent];
  const activeClassName = {
    purple: idleClassName,
    rose: idleClassName,
    emerald: idleClassName,
  }[accent];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={tooltip ?? title}>
        <NavLink
          to={to}
          onClick={onClick}
          active={active}
          className={cn(
            'group flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-[gap,background-color,color,border-color]',
            idleClassName,
            destinationHoverClassName,
            collapsedNavItemClass,
          )}
          activeClassName={activeClassName}
        >
          <Icon className={cn('h-4 w-4 shrink-0 transition-colors', palette.iconClass, destinationIconHoverClassName)} />
          <span
            aria-hidden={collapsed}
            className={cn(
              'overflow-hidden whitespace-nowrap transition-[max-width,opacity,transform,color] duration-200 ease-linear',
              palette.textMutedClass,
              destinationIconHoverClassName,
              labelClassName,
            )}
          >
            {title}
          </span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SurfaceSidebarProfileMenu({
  tone,
  collapsed,
  avatarLabel,
  avatarSrc,
  initials,
  name,
  roleLabel,
  badges,
  onAvatarClick,
  avatarAriaLabel,
  profileTriggerAriaLabel,
  profileActionsTestId,
  menuWidthClassName = 'w-56',
  copyButton,
  children,
}: {
  tone: SurfaceSidebarTone;
  collapsed: boolean;
  avatarLabel?: string;
  avatarSrc?: string;
  initials: string;
  name?: string | null;
  roleLabel: string;
  badges?: ReactNode;
  onAvatarClick: () => void;
  avatarAriaLabel: string;
  profileTriggerAriaLabel?: string;
  profileActionsTestId?: string;
  menuWidthClassName?: string;
  copyButton?: {
    ariaLabel: string;
    onClick: () => void;
  };
  children: ReactNode;
}) {
  const config = getSurfaceSidebarToneConfig(tone);

  return (
    <DropdownMenu>
      {collapsed ? (
        <DropdownMenuTrigger className="w-full">
          <div className="flex w-full items-center justify-center">
            <div className={cn('flex size-8 items-center justify-center rounded-md p-2 transition-colors', config.collapsedMenuButtonHoverClass)}>
              <ChevronDown className={cn('h-4 w-4', config.collapsedMenuIconClass)} />
            </div>
          </div>
        </DropdownMenuTrigger>
      ) : (
        <div data-testid={profileActionsTestId} className="group relative flex items-center gap-3">
          <button
            type="button"
            aria-label={avatarAriaLabel}
            className={cn('group rounded-full transition-all hover:scale-[1.02]', config.profileHoverClass)}
            onClick={onAvatarClick}
          >
            <Avatar className={cn('h-10 w-10 border transition-all group-hover:shadow-sm', config.profileAvatarBorderClass)}>
              <AvatarImage src={avatarSrc} alt={avatarLabel} />
              <AvatarFallback className={config.profileAvatarFallbackClass}>{initials}</AvatarFallback>
            </Avatar>
          </button>
          <DropdownMenuTrigger asChild>
            <div
              aria-label={profileTriggerAriaLabel}
              role="button"
              tabIndex={0}
              className={cn(
                'group flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors',
                config.profileHoverClass,
              )}
            >
              <SurfaceSidebarIdentityRow
                tone={tone}
                title={name}
                subtitle={roleLabel}
                badges={badges}
                trailingAccessory={copyButton ? (
                  <SurfaceSidebarCopyButton
                    tone={tone}
                    ariaLabel={copyButton.ariaLabel}
                    onClick={copyButton.onClick}
                    className="shrink-0"
                  />
                ) : undefined}
                titleClassName={cn('font-medium', config.profileNameClass)}
                subtitleClassName={config.profileRoleClass}
                chevronClassName={config.profileChevronClass}
              />
            </div>
          </DropdownMenuTrigger>
        </div>
      )}
      <DropdownMenuContent align="start" className={menuWidthClassName}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SurfaceSidebarToggleButton({
  expanded,
  onClick,
  className,
}: {
  expanded: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={expanded ? 'Hide sidebar menu' : 'Show sidebar menu'}
      onClick={onClick}
      className={className}
    >
      <PanelLeft className="h-4 w-4" />
    </button>
  );
}

export function SurfaceSidebarContextToggleButton({
  className,
}: {
  className?: string;
}) {
  const { state, toggleSidebar } = useSidebar();

  return (
    <SurfaceSidebarToggleButton
      expanded={state === 'expanded'}
      onClick={toggleSidebar}
      className={className}
    />
  );
}
